import {
  // isQuestionIds,
  QuestionVariant,
  SlideData,
} from "../interfaces/question.interface";
import { tryToParseStorageKeyValue, setStorageElement } from "./webStorage";
import {
  Profile,
  Age,
  BadgeId,
  QuestionId,
  Question,
  TranslationData,
  QuizModuleId,
  // quizModuleIds,
  QuizModule,
} from "@common";
import {
  badgeIds,
  badges,
  // questions
} from "@common";
import { renderTemplateComponents } from "../config";
import { TestTakerSettings } from "@natklok/contexts/testTakerSettingsContext";
import {
  getStoredQuizModuleDatas,
  SerializedQuizModuleData,
  QuizModuleData,
  getSerializedQuizModuleData,
} from "./quizModuleUtils";

export const getStoredQuestions = ({
  correctOrIncorrect,
  profiles,
  age,
  badgeId,
  questions,
}: {
  correctOrIncorrect: "correct" | "incorrect";
  profiles: Array<Profile>;
  age: Age;
  badgeId?: BadgeId;
  questions: Question[];
}): Array<SlideData> => {
  const storedArr = tryToParseStorageKeyValue(`${correctOrIncorrect}Questions`);
  // if (!isQuestionIds(storedArr)) return [];
  const foundQuestions: Array<SlideData> = [];
  const matchingVariants: Array<QuestionVariant> = [];
  storedArr.forEach((questionId: string) => {
    //Find question with correct questionId
    const correctlyAnsweredQuestion = questions.find((q: Question) =>
      Object.values(q.id).includes(questionId)
    );
    if (correctlyAnsweredQuestion) {
      if (
        questionMatchesSettings(profiles, age, correctlyAnsweredQuestion) &&
        !matchingVariants.includes(correctlyAnsweredQuestion?.variant.sv)
      ) {
        if (badgeId) {
          if (correctlyAnsweredQuestion?.badge === badgeId) {
            matchingVariants.push(correctlyAnsweredQuestion?.variant.sv);
            foundQuestions.push({
              question: correctlyAnsweredQuestion,
              state: correctOrIncorrect,
              completed: true,
              savedInStorage: true,
            });
          }
        } else {
          matchingVariants.push(correctlyAnsweredQuestion?.variant.sv);
          foundQuestions.push({
            question: correctlyAnsweredQuestion,
            state: correctOrIncorrect,
            completed: true,
            savedInStorage: true,
          });
        }
      }
    }
  });
  return foundQuestions;
};

export const getQuestionFromId = ({
  questionId,
  questions,
}: {
  questionId: string;
  questions: Question[];
}): Question => {
  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    if (question.id.sv === questionId || question.id.en === questionId) {
      return question;
    }
  }
  return questions[0];
};

export const getQuestionFromVariant = ({
  variantInSvOrEn,
  age,
  questions,
}: {
  variantInSvOrEn: QuestionVariant;
  age: Age;
  questions: Question[];
}): Question | undefined => {
  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    if (
      (question.variant.sv === variantInSvOrEn || question.variant.en === variantInSvOrEn) &&
      question.fitsAges.includes(age)
    ) {
      return question;
    }
  }
  return undefined;
};

export const countQuestionsInStorage = ({
  correctOrIncorrect,
  profiles,
  age,
  badgeId,
  questions,
}: {
  correctOrIncorrect: "correct" | "incorrect";
  profiles: Array<Profile>;
  age: Age;
  badgeId?: BadgeId;
  questions: Question[];
}): number => {
  return getStoredQuestions({ correctOrIncorrect, profiles, age, badgeId, questions }).length;
};

export const searchQuestionInStorage = (
  correctOrIncorrect: "correct" | "incorrect",
  questionId: TranslationData
): boolean => {
  const questionIdSv: QuestionId = questionId.sv;
  const questionIdEn: QuestionId = questionId.en;
  const storedArr = tryToParseStorageKeyValue(`${correctOrIncorrect}Questions`);
  // if (!isQuestionIds(storedArr)) return false;
  if (storedArr.includes(questionIdSv) || storedArr.includes(questionIdEn)) return true;
  return false;
};

export const getTotalAmountCorrectAnswers = () => {
  const correctVariants = getAllCorrectVariants();
  return correctVariants.length;
};

export const getCompletedQuizModuleIds = ({
  settings,
  questions,
  quizModules,
}: {
  settings: TestTakerSettings;
  questions: Question[];
  quizModules: Record<string, QuizModule>;
}): Array<QuizModuleId> => {
  return getStoredQuizModuleDatas({ quizModules })
    .filter((qm) => qm.getIsCompleted({ settings, questions, quizModules }))
    .map((qm) => qm.getId());
};

// UNUSED
// export const getAllQuizzesCompleted = ({
//   settings,
//   questions,
//   quizModules,
//   quizModuleIds,
// }: {
//   settings: TestTakerSettings;
//   questions: Question[];
//   quizModules: Record<string, QuizModule>;
//   quizModuleIds: string[];
// }) => {
//   return (
//     getCompletedQuizModuleIds({ settings, questions, quizModules }).length === quizModuleIds.length
//   );
// };

export const getCompletedQuizModules = ({
  settings,
  questions,
  quizModules,
}: {
  settings: TestTakerSettings;
  questions: Question[];
  quizModules: Record<string, QuizModule>;
}): Array<QuizModuleData> => {
  return getStoredQuizModuleDatas({ quizModules }).filter((qm) =>
    qm.getIsCompleted({ settings, questions, quizModules })
  );
};

export const getAttemptedQuizModuleIds = ({
  quizModules,
}: {
  quizModules: Record<string, QuizModule>;
}): Array<QuizModuleId> => {
  return getStoredQuizModuleDatas({ quizModules }).map((qm) => qm.getId());
};

// UNUSED
// export const getAttemptedQuizModules = ({
//   quizModules,
// }: {
//   quizModules: Record<string, QuizModule>;
// }): Array<QuizModuleData> => {
//   return getStoredQuizModuleDatas({ quizModules });
// };

export const getUncompletedQuizModules = ({
  settings,
  questions,
  quizModules,
}: {
  settings: TestTakerSettings;
  questions: Question[];
  quizModules: Record<string, QuizModule>;
}): Array<QuizModuleData> => {
  const completed = getCompletedQuizModuleIds({ settings, questions, quizModules });
  return getStoredQuizModuleDatas({ quizModules }).filter(
    (qm) =>
      qm.getIsUncompleted({ settings, questions, quizModules }) && !completed.includes(qm.getId())
  );
};

export const addAnswerToStorage = ({
  correctOrIncorrect,
  question,
  quizModuleId,
  quizModules,
}: {
  correctOrIncorrect: "correct" | "incorrect";
  question: Question;
  quizModuleId: QuizModuleId | "";
  quizModules: Record<string, QuizModule>;
}): boolean => {
  if (quizModuleId === "") return false;

  const serializedQuizModuleData = getSerializedQuizModuleData();

  const questionId = question.id.sv;

  const allCorrectVariants = tryToParseStorageKeyValue(`correct-variants3`);
  if (allCorrectVariants === null) {
    setStorageElement("correct-variants3", [question.variant.sv]);
  } else if (correctOrIncorrect === "correct") {
    const newCorrectVariants = [...allCorrectVariants];
    const correctQuestionVariant = question.variant.sv;
    if (!newCorrectVariants.includes(correctQuestionVariant)) {
      newCorrectVariants.push(correctQuestionVariant);
      setStorageElement("correct-variants3", newCorrectVariants);
    }
  }

  if (serializedQuizModuleData !== null) {
    const newSerializedQuizModuleData: Array<SerializedQuizModuleData> = [
      ...serializedQuizModuleData,
    ];
    let modifiedExistingModuleData = false;

    for (let i = 0; i < newSerializedQuizModuleData.length; i++) {
      const quizModuleData = new QuizModuleData({
        moduleData: newSerializedQuizModuleData[i],
        quizModules,
      });
      if (quizModuleData.getId() === quizModuleId) {
        modifiedExistingModuleData = true;

        // We won't move a incorrectly answered question to the incorrect array if they've already answered it correctly
        if (quizModuleData.hasCorrectlyAnswered(question.id.sv) === false) {
          quizModuleData.addCorrectOrIncorrect(correctOrIncorrect, questionId);
          newSerializedQuizModuleData[i] = quizModuleData.getSerialized();
        }
        break;
      }
    }

    if (modifiedExistingModuleData === false) {
      const moduleData = new SerializedQuizModuleData(quizModuleId);
      const quizModuleData = new QuizModuleData({ moduleData, quizModules });
      quizModuleData.addCorrectOrIncorrect(correctOrIncorrect, questionId);
      newSerializedQuizModuleData.push(quizModuleData.getSerialized());
    }

    return setStorageElement("quizModules3", newSerializedQuizModuleData);
  }

  // Create a new array to store if one didn't already exist
  const moduleData = new SerializedQuizModuleData(quizModuleId);
  const quizModuleData = new QuizModuleData({ moduleData, quizModules });
  quizModuleData.addCorrectOrIncorrect(correctOrIncorrect, questionId);
  return setStorageElement("quizModules3", [quizModuleData]);
};

export const getAllCorrectVariants = (): Array<QuestionVariant> => {
  const allCorrectVariants = tryToParseStorageKeyValue(`correct-variants3`);

  return allCorrectVariants === null ? [] : allCorrectVariants;
};

export const getHasCompletedAllCorrectVariants = ({
  age,
  questions,
}: {
  age: Age;
  questions: Question[];
}): boolean => {
  const allVariants: QuestionVariant[] = [];
  for (let j = 0; j < questions.length; j++) {
    if (
      allVariants.includes(questions[j].variant.sv) === false &&
      questions[j].fitsAges.includes(age)
    ) {
      allVariants.push(questions[j].variant.sv);
    }
  }
  const allCorrectVariants = tryToParseStorageKeyValue(`correct-variants3`);
  const variants: QuestionVariant[] = allCorrectVariants === null ? [] : allCorrectVariants;
  return variants.length >= allVariants.length;
};

// export const getLangFromQuestionVariant = (variant: QuestionVariant): "sv" | "en" => {
//   let lang = "";
//   questions.forEach((question: Question) => {
//     if (question.variant.sv === variant) lang = "sv";
//     else if (question.variant.en === variant) lang = "en";
//     return lang;
//   });
//   return "sv";
// };

export const getLangFromBadgePath = (badgePath: string) => {
  for (const translationData of Object.values(badges)) {
    if (translationData.sv === badgePath) return "sv";
    else if (translationData.en === badgePath) return "en";
  }
  return "sv";
};

export const getNOfMatchingQuestions = ({
  profiles,
  age,
  questions,
}: {
  profiles: Array<Profile>;
  age: Age | "";
  questions: Question[];
}): {
  count: number;
  badgeIdCounts: Record<BadgeId, number>;
} => {
  if (age === "") {
    age = "adult";
  }
  const questionVariants: Set<QuestionVariant> = new Set();
  let nOfMatchingQuestions = 0;
  const nOfMatchingQuestionsInBadges: Record<BadgeId, number> = {};
  for (const badgeId of badgeIds) {
    nOfMatchingQuestionsInBadges[badgeId] = 0;
  }
  for (const question of questions) {
    if (
      question.fitsAges.includes(age) &&
      profiles.some((profile) => question.fitsProfiles.includes(profile)) &&
      !questionVariants.has(question.variant.sv)
    ) {
      nOfMatchingQuestions++;
      nOfMatchingQuestionsInBadges[question.badge]++;
      questionVariants.add(question.variant.sv);
    }
  }

  return {
    count: nOfMatchingQuestions,
    badgeIdCounts: nOfMatchingQuestionsInBadges,
  };
};

export const getBadgeIdFromBadgePath = (path: string) => {
  for (const [badgeId, translationData] of Object.entries(badges)) {
    if ([translationData.sv, translationData.en].some((val) => val === path)) {
      return badgeId;
    }
  }
  return "";
};

export const getBadgePathFromBadgeId = (badge: BadgeId, lang: "sv" | "en") => {
  for (const [currBadge, translationData] of Object.entries(badges)) {
    if (currBadge === badge) {
      return translationData[lang];
    }
  }
  return "phishing";
};

// export const getVariantFromId = (id: QuestionId): TranslationData | null => {
//   let foundVariant = null;
//   for (const question of questions) {
//     const q: Question = question;
//     if (Object.values(q.id).includes(id)) {
//       foundVariant = q.variant;
//       break;
//     }
//   }
//   return foundVariant;
// };

export const questionMatchesSettings = (
  profiles: Array<Profile>,
  age: Age,
  question: Question,
  badgeIds?: Array<BadgeId>
): boolean => {
  let matches =
    question?.fitsAges.includes(age) &&
    profiles.some((profile) => question?.fitsProfiles.includes(profile));

  if (badgeIds !== undefined) {
    matches = matches && badgeIds.includes(question.badge);
  }

  return matches;
};

export const getRenderTemplateComponent = (
  question: Pick<Question, "renderTemplate" | "renderProps">,
  readOnly = false,
  showCorrect = false
) => {
  for (const [key, component] of Object.entries(renderTemplateComponents)) {
    if (key === question.renderTemplate)
      return component({ ...question.renderProps, readOnly: readOnly, showCorrect: showCorrect });
  }
  return null;
};

export const slideDataIsQuestion = (slideData: SlideData | undefined): boolean => {
  if (slideData === undefined) return false;
  return slideData.question !== undefined;
};

export const slideDataIsDiscussion = (slideData: SlideData | undefined): boolean => {
  if (slideData === undefined) return false;
  return slideData.discussion !== undefined;
};
